import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, User, Mail, Phone, MessageCircle, 
  Check, X, Loader2, RefreshCw, Filter,
  Bell, Send, Trash2, LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { pt } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const { logout, getAuthHeader } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const updateBookingStatus = async (id, status) => {
    setUpdating(id);
    try {
      await axios.put(`${API}/bookings/${id}`, { status });
      await fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
    setUpdating(null);
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`);
      await fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
    }
  };

  // Generate WhatsApp message links
  const generateWhatsAppLink = (booking, type) => {
    const phone = booking.phone.replace(/[^0-9]/g, '');
    const phoneWithCountry = phone.startsWith('351') ? phone : `351${phone}`;
    
    let message = '';
    
    if (type === 'confirmation') {
      message = `Olá ${booking.name}! 👋

Obrigada pelo seu pedido de marcação na *Shape & Shine*! ✨

📅 *Data:* ${format(parseISO(booking.date), "EEEE, d 'de' MMMM", { locale: pt })}
🕐 *Hora:* ${booking.time}
💆 *Serviço:* ${booking.service}

Confirmo a sua marcação? Por favor responda SIM para confirmar ou sugira outra data/hora se necessário.

Aguardo a sua resposta! 💕

Sandra Silva
Shape & Shine`;
    } else if (type === 'reminder') {
      message = `Olá ${booking.name}! 👋

Este é um lembrete amigável da sua sessão *amanhã* na Shape & Shine! ✨

📅 *Data:* ${format(parseISO(booking.date), "EEEE, d 'de' MMMM", { locale: pt })}
🕐 *Hora:* ${booking.time}
💆 *Serviço:* ${booking.service}

📍 *Morada:* Rua Tomé Barros Queirós n°34, 3810-833 Oliveirinha

Caso precise reagendar, por favor avise-nos com antecedência.

Até amanhã! 💕

Sandra Silva
Shape & Shine`;
    } else if (type === 'today') {
      message = `Olá ${booking.name}! 👋

Lembrete: A sua sessão é *hoje*! ✨

🕐 *Hora:* ${booking.time}
💆 *Serviço:* ${booking.service}

📍 *Morada:* Rua Tomé Barros Queirós n°34, 3810-833 Oliveirinha

Esperamos por si! 💕

Sandra Silva
Shape & Shine`;
    }
    
    return `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    const labels = {
      pending: 'Pendente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Concluída',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return { label: 'HOJE', color: 'text-red-400' };
    if (isTomorrow(date)) return { label: 'AMANHÃ', color: 'text-yellow-400' };
    return { label: format(date, "d MMM", { locale: pt }), color: 'text-[#bbb5ae]' };
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    if (filter === 'pending') return booking.status === 'pending';
    if (filter === 'confirmed') return booking.status === 'confirmed';
    if (filter === 'today') return isToday(parseISO(booking.date));
    if (filter === 'tomorrow') return isTomorrow(parseISO(booking.date));
    return true;
  });

  // Count bookings needing attention
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const todayCount = bookings.filter(b => isToday(parseISO(b.date)) && b.status === 'confirmed').length;
  const tomorrowCount = bookings.filter(b => isTomorrow(parseISO(b.date)) && b.status === 'confirmed').length;

  // Generate bulk WhatsApp reminder link for tomorrow's bookings
  const generateBulkReminders = () => {
    const tomorrowBookings = bookings.filter(b => isTomorrow(parseISO(b.date)) && b.status === 'confirmed');
    if (tomorrowBookings.length === 0) return null;
    
    // Open first reminder - user can send and come back for more
    return tomorrowBookings.map(b => ({
      name: b.name,
      link: generateWhatsAppLink(b, 'reminder')
    }));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f0e8]">
      {/* Header */}
      <header className="bg-[#111111] border-b border-[#C1A54F]/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl text-gold-gradient">Painel de Administração</h1>
            <p className="text-[#bbb5ae] text-sm">Shape & Shine - Gestão de Marcações</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-[#E5B1C4] hover:text-[#FADCD9] text-sm">
              ← Voltar ao site
            </a>
            <Button 
              onClick={logout}
              variant="outline" 
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#111111] border border-yellow-500/20 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <Bell className="text-yellow-400" size={24} />
              <span className="text-2xl font-bold text-yellow-400">{pendingCount}</span>
            </div>
            <p className="text-[#bbb5ae] text-sm mt-2">Pendentes</p>
          </div>
          <div className="bg-[#111111] border border-red-500/20 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <Calendar className="text-red-400" size={24} />
              <span className="text-2xl font-bold text-red-400">{todayCount}</span>
            </div>
            <p className="text-[#bbb5ae] text-sm mt-2">Hoje</p>
          </div>
          <div className="bg-[#111111] border border-orange-500/20 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <Clock className="text-orange-400" size={24} />
              <span className="text-2xl font-bold text-orange-400">{tomorrowCount}</span>
            </div>
            <p className="text-[#bbb5ae] text-sm mt-2">Amanhã</p>
          </div>
          <div className="bg-[#111111] border border-[#C1A54F]/20 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <Check className="text-[#E1C57C]" size={24} />
              <span className="text-2xl font-bold text-[#E1C57C]">{bookings.length}</span>
            </div>
            <p className="text-[#bbb5ae] text-sm mt-2">Total</p>
          </div>
        </div>

        {/* Quick Actions - Send Reminders */}
        {tomorrowCount > 0 && (
          <div className="mb-8 p-4 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-orange-400 font-medium flex items-center gap-2">
                  <Bell size={18} />
                  Lembretes para Amanhã
                </h3>
                <p className="text-[#bbb5ae] text-sm mt-1">
                  {tomorrowCount} cliente(s) com sessão confirmada para amanhã
                </p>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Send size={16} className="mr-2" />
                    Enviar Lembretes
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#111111] border-[#C1A54F]/20 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-[#f5f0e8]">Enviar Lembretes</DialogTitle>
                    <DialogDescription className="text-[#bbb5ae]">
                      Clique em cada cliente para abrir o WhatsApp com a mensagem de lembrete pré-escrita.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    {generateBulkReminders()?.map((item, index) => (
                      <a
                        key={index}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-[#C1A54F]/10 rounded hover:border-[#25D366]/50 transition-colors"
                      >
                        <span className="text-[#f5f0e8]">{item.name}</span>
                        <MessageCircle size={18} className="text-[#25D366]" />
                      </a>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        )}

        {/* Filters & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] bg-[#111111] border-[#C1A54F]/20">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-[#C1A54F]/20">
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="confirmed">Confirmadas</SelectItem>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="tomorrow">Amanhã</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={fetchBookings} variant="outline" className="border-[#C1A54F]/30 text-[#E1C57C]">
            <RefreshCw size={16} className="mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#E1C57C] animate-spin" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-20 text-[#bbb5ae]">
            <Calendar size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhuma marcação encontrada</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => {
              const dateInfo = getDateLabel(booking.date);
              return (
                <div 
                  key={booking.id} 
                  className="bg-[#111111] border border-[#C1A54F]/10 rounded-sm p-6 hover:border-[#C1A54F]/30 transition-all"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    {/* Client Info */}
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-3 mb-2">
                        <User size={18} className="text-[#E5B1C4]" />
                        <span className="font-medium text-lg">{booking.name}</span>
                        {getStatusBadge(booking.status)}
                      </div>
                      <div className="space-y-1 text-sm text-[#bbb5ae]">
                        <div className="flex items-center gap-2">
                          <Mail size={14} />
                          <a href={`mailto:${booking.email}`} className="hover:text-[#E5B1C4]">{booking.email}</a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone size={14} />
                          <a href={`tel:${booking.phone}`} className="hover:text-[#E5B1C4]">{booking.phone}</a>
                        </div>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="flex-1 min-w-[200px]">
                      <div className={`font-bold mb-1 ${dateInfo.color}`}>
                        {dateInfo.label} às {booking.time}
                      </div>
                      <div className="text-[#E1C57C] font-medium">{booking.service}</div>
                      {booking.message && (
                        <div className="mt-2 text-sm text-[#bbb5ae] italic">
                          "{booking.message}"
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {/* WhatsApp Actions */}
                      <div className="flex gap-2">
                        <a
                          href={generateWhatsAppLink(booking, 'confirmation')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-2 bg-[#25D366] hover:bg-[#128C7E] text-white text-xs rounded transition-colors"
                        >
                          <MessageCircle size={14} />
                          Confirmar
                        </a>
                        {(isToday(parseISO(booking.date)) || isTomorrow(parseISO(booking.date))) && (
                          <a
                            href={generateWhatsAppLink(booking, isTomorrow(parseISO(booking.date)) ? 'reminder' : 'today')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-2 bg-[#075E54] hover:bg-[#128C7E] text-white text-xs rounded transition-colors"
                          >
                            <Bell size={14} />
                            Lembrete
                          </a>
                        )}
                      </div>

                      {/* Status Actions */}
                      <div className="flex gap-2">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              disabled={updating === booking.id}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3"
                            >
                              {updating === booking.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              disabled={updating === booking.id}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs px-3"
                            >
                              <X size={14} />
                            </Button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <Button
                            size="sm"
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            disabled={updating === booking.id}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3"
                          >
                            Concluir
                          </Button>
                        )}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs px-3">
                              <Trash2 size={14} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-[#111111] border-[#C1A54F]/20">
                            <DialogHeader>
                              <DialogTitle className="text-[#f5f0e8]">Eliminar Marcação</DialogTitle>
                              <DialogDescription className="text-[#bbb5ae]">
                                Tem a certeza que deseja eliminar esta marcação? Esta ação não pode ser desfeita.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button variant="outline" className="border-[#C1A54F]/30">Cancelar</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button onClick={() => deleteBooking(booking.id)} className="bg-red-600 hover:bg-red-700">
                                  Eliminar
                                </Button>
                              </DialogClose>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
